import { useMemo } from 'react';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTableAssets from '@elementary/common-ui/hooks/useTableAssets';
import { Report, TestResult } from '@elementary/common-ui/types';
import { distinctArrayBy } from '@elementary/common-ui/utils/common';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import {
  parseReportTestResult,
  type TestResultsTableData,
} from '@elementary/common-ui/components';

const getDistinctAndSortedTestResults = (
  testResults: TestResult[],
  key: string,
): TestResult[] => {
  const distinctResults = distinctArrayBy(testResults, key);
  return distinctResults.sort((a, b) =>
    (a.metadata.elementary_unique_id || '').localeCompare(
      b.metadata.elementary_unique_id || '',
    ),
  );
};

const useReportTests = ({
  reportData,
  enabled = true,
  onError,
}: {
  reportData?: Report;
  enabled?: boolean;
  onError?: () => void;
  search?: string;
}) => {
  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();

  const { urlTreeNode, urlSavedView } = useTreeViewParams();

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const testResults = useMemo(
    () => reportData?.test_results,
    [reportData?.test_results],
  );

  const modelsData = useMemo(
    () => reportData?.models || {},
    [reportData?.models],
  );

  const { data: tableAssetsExtraDataMap, isFetching: isTableAssetsFetching } =
    useTableAssets({ enabled: enabled, onError: onError });

  const testResultsData = useMemo(() => {
    if (!testResults || !tableAssetsExtraDataMap) return testResults;

    const resultsData: Record<string, TestResult[]> = {};

    Object.keys(testResults).forEach((modelKey) => {
      resultsData[modelKey] = testResults[modelKey].map((testResult) => {
        if (tableAssetsExtraDataMap.size === 0) {
          // OSS mode
          const { tags, owners } =
            modelsData[testResult.metadata.model_unique_id || ''] || {};

          return {
            ...testResult,
            asset_metadata: {
              tags,
              owners,
            },
          };
        }

        // SaaS mode
        const tableAssetExtraData = tableAssetsExtraDataMap.get(
          testResult.metadata.model_unique_id || '',
        );

        return {
          ...testResult,
          asset_metadata: {
            last_data_modification_time:
              tableAssetExtraData?.lastDataModificationTime || '',
            materialization: tableAssetExtraData?.materialization || '',
            tags: tableAssetExtraData?.tags || [],
            owners: tableAssetExtraData?.owners || [],
            is_critical: tableAssetExtraData?.isCritical || false,
            path: tableAssetExtraData?.path || '',
            name: tableAssetExtraData?.name || '',
            subscribers: tableAssetExtraData?.subscribers || [],
          },
        };
      });
    });
    return resultsData;
  }, [modelsData, tableAssetsExtraDataMap, testResults]);

  const allTestResults = useMemo(() => {
    const allTests: TestResult[] = [];
    if (!testResultsData) return allTests;

    Object.keys(testResultsData).forEach((modelKey) => {
      allTests.push(...(testResultsData[modelKey] || []));
    });

    return getDistinctAndSortedTestResults(
      allTests,
      'metadata.elementary_unique_id',
    );
  }, [testResultsData]);

  const distinctTestResults = useMemo(() => {
    if (!testResultsData || !selectedTreeNodeIds) return allTestResults;

    const testResults: TestResult[] = [];

    selectedTreeNodeIds.forEach((nodeId) =>
      testResults.push(...(testResultsData[nodeId] || [])),
    );

    return getDistinctAndSortedTestResults(
      testResults,
      'metadata.elementary_unique_id',
    );
  }, [testResultsData, selectedTreeNodeIds, allTestResults]);

  const tableData = useMemo<TestResultsTableData[]>(() => {
    if (!distinctTestResults) return [];
    return distinctTestResults.map(parseReportTestResult);
  }, [distinctTestResults]);

  return {
    tableData,
    filterOptions: null,
    isFetching: isTableAssetsFetching,
    isFilterOptionsFetching: false,
    fetchNextPage: undefined,
    hasNextPage: false,
    isError: false,
  };
};

export default useReportTests;
